/**
 * Sidebar — navigation verticale premium qui s'ouvre au hover.
 *
 * Architecture menus (refonte 2026-06-04) :
 *
 *   🎯 Tableau de bord       (item direct)
 *   🔍 Prospection           ▼ Audit · Pipeline · Relances · Séquences DM
 *   ✨ Contenu & Marque       ▼ Stratégie · Studio · Bibliothèque · Identité
 *   🤖 Inéa & Coach          ▼ Diagnostic · Conversations · Paramètres
 *   🔌 Connexions            ▼ Comptes sociaux · Connecteurs
 *   ────
 *   🌐 Langue · 🚪 Déconnexion
 *
 * Pattern accordion (clic = déplie sous-menus). 1 menu actif à la fois.
 * Auto-ouvre le menu qui contient la route courante.
 *
 * Collapsed (76px) : icônes seules + tooltips.
 * Expanded (240px) : icônes + labels, sous-menus visibles.
 *
 * Toujours visible sur desktop, cachée sur mobile (< sm).
 */
import { useState, useEffect, useRef } from 'react';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useAutomationsAccess } from '@/lib/automations';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  LayoutGrid,
  UserSearch,
  ClipboardList,
  Palette,
  CalendarDays,
  Compass,
  Wand2,
  BookOpen,
  BellRing,
  LogOut,
  ChevronRight,
  ChevronDown,
  Globe2,
  Check,
  Plug,
  Workflow,
  Cable,
  MessageSquare,
  Settings2,
  Sparkles,
  Target,
  Bot,
  CalendarClock,
  Zap,
  LayoutTemplate,
  Package,
  BarChart3,
  SlidersHorizontal,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useFeatures } from '@/hooks/useFeatures';
import { IneaMark } from '@/components/IneaMark';
import { SUPPORTED_LANGS, type LangCode } from '@/i18n';

/* ─── Nav structure ──────────────────────────────────────────────────── */

interface NavChild {
  to: string;
  i18nKey: string;
  icon: typeof LayoutGrid;
  tint: string;
}

interface NavGroup {
  /** Stable id used for accordion state. */
  id: string;
  /** Translation key for the group label. */
  i18nKey: string;
  icon: typeof LayoutGrid;
  /** Color used for the group's accent stripe. */
  tint: string;
  children: NavChild[];
}

/** Single top-level item (no accordion). */
const HOME_ITEM: NavChild = {
  to: '/',
  i18nKey: 'nav.dashboard',
  icon: LayoutGrid,
  tint: '#a855f7',
};

/** Lot 7 (2026-10-29) — espace M&R (origine CRM) : les conversations en entrée directe ; seuls les groupes utiles restent. */
const CONVERSATIONS_ITEM: NavChild = { to: '/conversations', i18nKey: 'nav.conversations', icon: MessageSquare, tint: '#06b6d4' };
const MR_GROUPS = new Set(['content', 'automations', 'connections']);

const GROUPS: NavGroup[] = [
  {
    id: 'prospecting',
    i18nKey: 'nav.groups.prospecting',
    icon: Target,
    tint: '#ec4899',
    children: [
      { to: '/audit', i18nKey: 'nav.audit', icon: UserSearch, tint: '#ec4899' },
      { to: '/prospects', i18nKey: 'nav.pipeline', icon: ClipboardList, tint: '#a855f7' },
      { to: '/relances', i18nKey: 'nav.followups', icon: BellRing, tint: '#f59e0b' },
      { to: '/sequences', i18nKey: 'nav.sequences', icon: Workflow, tint: '#8b5cf6' },
    ],
  },
  {
    id: 'content',
    i18nKey: 'nav.groups.content',
    icon: Sparkles,
    tint: '#a855f7',
    children: [
      { to: '/contenu', i18nKey: 'nav.content', icon: CalendarDays, tint: '#f59e0b' },
      { to: '/studio', i18nKey: 'nav.studio', icon: Wand2, tint: '#ec4899' },
      { to: '/bibliotheque', i18nKey: 'nav.library', icon: BookOpen, tint: '#06b6d4' },
      { to: '/identite', i18nKey: 'nav.identity', icon: Palette, tint: '#3b82f6' },
      { to: '/calendrier', i18nKey: 'nav.calendar', icon: CalendarClock, tint: '#a855f7' },
    ],
  },
  {
    id: 'inea',
    i18nKey: 'nav.groups.inea',
    icon: Bot,
    tint: '#8b5cf6',
    children: [
      { to: '/diagnostic', i18nKey: 'nav.diagnostic', icon: Compass, tint: '#a855f7' },
      { to: '/conversations', i18nKey: 'nav.conversations', icon: MessageSquare, tint: '#a855f7' },
      { to: '/parametres/inea', i18nKey: 'nav.ineaSettings', icon: Settings2, tint: '#3b82f6' },
    ],
  },
  {
    // Module Automatisations (lot 1a, 2026-10-28) — visible seulement si automations_enabled.
    id: 'automations',
    i18nKey: 'nav.groups.automations',
    icon: Workflow,
    tint: '#8b5cf6',
    children: [
      { to: '/automatisations', i18nKey: 'nav.automations', icon: Zap, tint: '#8b5cf6' },
      { to: '/automatisations/modeles', i18nKey: 'nav.automationTemplates', icon: LayoutTemplate, tint: '#a855f7' },
      { to: '/automatisations/produits', i18nKey: 'nav.products', icon: Package, tint: '#ec4899' },
      { to: '/automatisations/statistiques', i18nKey: 'nav.automationStats', icon: BarChart3, tint: '#06b6d4' },
      { to: '/automatisations/reglages', i18nKey: 'nav.automationSettings', icon: SlidersHorizontal, tint: '#3b82f6' },
    ],
  },
  {
    id: 'connections',
    i18nKey: 'nav.groups.connections',
    icon: Plug,
    tint: '#10b981',
    children: [
      { to: '/comptes', i18nKey: 'nav.accounts', icon: Plug, tint: '#10b981' },
      { to: '/connexion-sociale', i18nKey: 'nav.social', icon: Plug, tint: '#10b981' },
      { to: '/integrations', i18nKey: 'nav.integrations', icon: Cable, tint: '#f59e0b' },
    ],
  },
];

/** Maps any pathname back to the group it belongs to, or null for /. */
function findGroupForPath(pathname: string): string | null {
  for (const g of GROUPS) {
    if (g.children.some((c) => pathname.startsWith(c.to))) return g.id;
  }
  return null;
}

/* ─── Component ──────────────────────────────────────────────────────── */

/** Entrées masquées tant que META_FEATURES_ENABLED=false (voir /config). */
const HIDDEN_WITHOUT_META = new Set(['/connexion-sociale', '/sequences', '/integrations']);
/** Entrées qui n'existent que si la publication est allumée (Lot 2, flag publishing_enabled). */
const HIDDEN_WITHOUT_PUBLISHING = new Set(['/calendrier', '/comptes']);
/** Module Automatisations (lot 1a, 2026-10-28) : le groupe entier dépend du drapeau automations_enabled. */
const HIDDEN_WITHOUT_AUTOMATIONS = new Set(['/automatisations', '/automatisations/modeles', '/automatisations/produits', '/automatisations/statistiques', '/automatisations/reglages']);

export function Sidebar() {
  const { logout, user } = useAuth();
  const mrSpace = user?.origin === 'mr_crm'; // lot 7 — espace M&R : vue allégée (prospection et coaching masqués)
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { meta_features_enabled: metaEnabled, publishing_enabled: publishingEnabled, automations_enabled: automationsEnabled } = useFeatures();
  const { role: workspaceRole } = useWorkspace(); // lot 1d — un conseiller ne voit pas le groupe Automatisations
  // Lot 6 (2026-10-29) — le groupe n'apparaît que pour un espace de l'allow-list (bêta privée) : un partenaire Mars
  // hors liste ne voit pas un menu dont chaque page répondrait « introuvable ».
  const access = useAutomationsAccess(automationsEnabled === true);
  const automationsVisible = automationsEnabled === true && workspaceRole !== 'conseiller' && access.data?.enabled === true;
  // Lot A (2026-07-20) — META masqué : l'entrée « Comptes sociaux » disparaît.
  // Lot 1 (2026-09-03) — Séquences et Connecteurs masqués tant que META est éteint.
  // Lot 2 (2026-09-03) — Calendrier et Comptes n'existent que si la publication est allumée.
  const groups = (mrSpace ? GROUPS.filter((g) => MR_GROUPS.has(g.id)) : GROUPS).map((g) => ({
    ...g,
    children: g.children.filter(
      (c) => (metaEnabled || !HIDDEN_WITHOUT_META.has(c.to)) && (publishingEnabled || !HIDDEN_WITHOUT_PUBLISHING.has(c.to)) && (automationsVisible || !HIDDEN_WITHOUT_AUTOMATIONS.has(c.to)),
    ),
  })).filter((g) => g.children.length > 0);
  const [hovered, setHovered] = useState(false);
  // Active accordion group. Auto-opens the group containing the current route.
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  // Whenever the route changes, sync the open group so the current page's
  // group is always expanded — but never close a group the user just opened.
  useEffect(() => {
    const matched = findGroupForPath(location.pathname);
    if (matched && matched !== activeGroup) setActiveGroup(matched);
    // We intentionally don't close on route change inside the same group.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (id: string) =>
    setActiveGroup((prev) => (prev === id ? null : id));

  return (
    <motion.aside
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      animate={{ width: hovered ? 248 : 76 }}
      transition={{ type: 'spring', stiffness: 320, damping: 32 }}
      className="fixed inset-y-3 left-3 z-30 hidden flex-col rounded-[28px] border border-ink/[0.08] bg-paper py-5 shadow-[0_28px_70px_-26px_rgba(168,139,250,0.36),0_10px_28px_-14px_rgba(236,72,153,0.18),inset_0_1px_0_rgba(255,255,255,0.95)] sm:flex"
      style={{
        backgroundImage:
          'linear-gradient(180deg, #FFFFFF 0%, #FFFAF0 60%, #F8F6F0 100%)',
      }}
      aria-label={t('nav.mainNav')}
      role="navigation"
    >
      {/* Header brand */}
      <button
        onClick={() => navigate('/')}
        className="group mx-auto mb-5 flex items-center gap-3 rounded-2xl px-2 py-1 transition hover:scale-[1.03]"
        aria-label="Network-iA"
      >
        <span className="relative shrink-0">
          <span
            className="absolute -inset-2 rounded-full opacity-50 blur-md transition group-hover:opacity-80"
            style={{ background: 'radial-gradient(circle, #a855f7, transparent 70%)' }}
          />
          <IneaMark size={42} />
        </span>
        <motion.span
          initial={false}
          animate={{ opacity: hovered ? 1 : 0, x: hovered ? 0 : -8 }}
          transition={{ duration: 0.18 }}
          className="whitespace-nowrap font-display text-[17px] font-700 leading-none text-ink"
        >
          Network-iA
        </motion.span>
      </button>

      {/* Nav — scrollable to handle small screens */}
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2.5 pb-2">
        {/* Dashboard (no subgroup) */}
        <NavLeaf item={HOME_ITEM} open={hovered} label={t(HOME_ITEM.i18nKey)} />
        {mrSpace && <NavLeaf item={CONVERSATIONS_ITEM} open={hovered} label={t(CONVERSATIONS_ITEM.i18nKey)} />}

        {/* Visual divider */}
        <div className="my-2 h-px bg-ink/[0.06]" />

        {/* Accordion groups */}
        {groups.map((g) => (
          <NavGroupAccordion
            key={g.id}
            group={g}
            open={hovered}
            isActive={activeGroup === g.id}
            currentPath={location.pathname}
            onToggle={() => toggleGroup(g.id)}
            t={t}
          />
        ))}
      </nav>

      {/* Language selector */}
      <div className="mt-2 px-2.5">
        <SidebarLanguagePicker open={hovered} />
      </div>

      {/* Logout */}
      <div className="mt-2 border-t border-ink/[0.07] px-2.5 pt-3">
        <button
          onClick={logout}
          className="group flex h-11 w-full items-center gap-3 rounded-2xl px-3 text-[13.5px] font-600 text-ink/55 transition hover:bg-pink/10 hover:text-pink-deep"
          aria-label={t('nav.logout')}
        >
          <LogOut size={17} className="shrink-0" />
          <motion.span
            initial={false}
            animate={{ opacity: hovered ? 1 : 0, x: hovered ? 0 : -6 }}
            transition={{ duration: 0.18 }}
            className="whitespace-nowrap"
          >
            {t('nav.logout')}
          </motion.span>
        </button>
      </div>
    </motion.aside>
  );
}

/* ─── Single leaf (Dashboard + sub-items) ────────────────────────────── */

function NavLeaf({
  item,
  open,
  label,
  inset = false,
}: {
  item: NavChild;
  open: boolean;
  label: string;
  inset?: boolean;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        `group relative flex h-10 items-center gap-3 rounded-2xl px-3 transition ${
          inset ? 'ml-3 h-9 text-[12.5px]' : 'h-11 text-[13.5px]'
        } ${isActive ? 'text-ink' : 'text-ink/55 hover:text-ink'}`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.span
              layoutId="sidebar-leaf-active"
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              className="absolute inset-0 rounded-2xl"
              style={{
                background: `linear-gradient(135deg, ${item.tint}22, ${item.tint}0a)`,
                boxShadow: `0 6px 18px -8px ${item.tint}88, inset 0 0 0 1px ${item.tint}55`,
              }}
            />
          )}
          <Icon
            size={inset ? 15 : 18}
            className="relative z-10 shrink-0"
            style={{ color: isActive ? item.tint : undefined }}
          />
          <motion.span
            initial={false}
            animate={{ opacity: open ? 1 : 0, x: open ? 0 : -6 }}
            transition={{ duration: 0.18 }}
            className="relative z-10 flex-1 whitespace-nowrap font-600"
          >
            {label}
          </motion.span>
          {isActive && open && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative z-10 mr-1"
            >
              <ChevronRight size={inset ? 12 : 14} style={{ color: item.tint }} />
            </motion.span>
          )}
          {!open && (
            <span
              className="pointer-events-none absolute left-full top-1/2 ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-night px-2.5 py-1.5 text-[11.5px] font-700 text-white opacity-0 shadow-lg transition group-hover:opacity-100"
            >
              {label}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/* ─── Accordion group (Prospection, Contenu, Inéa, Connexions) ───────── */

function NavGroupAccordion({
  group,
  open,
  isActive,
  currentPath,
  onToggle,
  t,
}: {
  group: NavGroup;
  open: boolean;
  isActive: boolean;
  currentPath: string;
  onToggle: () => void;
  t: (k: string) => string;
}) {
  const Icon = group.icon;
  // Highlight the group header if a child route is the current page.
  const containsCurrent = group.children.some((c) =>
    currentPath.startsWith(c.to),
  );

  return (
    <div className="flex flex-col">
      <button
        onClick={onToggle}
        className={`group relative flex h-11 items-center gap-3 rounded-2xl px-3 text-[13.5px] font-700 transition ${
          containsCurrent ? 'text-ink' : 'text-ink/65 hover:text-ink'
        }`}
        aria-expanded={isActive}
      >
        {containsCurrent && (
          <span
            className="absolute inset-0 rounded-2xl"
            style={{
              background: `linear-gradient(135deg, ${group.tint}15, ${group.tint}05)`,
              boxShadow: `inset 0 0 0 1px ${group.tint}40`,
            }}
          />
        )}
        <Icon
          size={18}
          className="relative z-10 shrink-0"
          style={{ color: containsCurrent ? group.tint : undefined }}
        />
        <motion.span
          initial={false}
          animate={{ opacity: open ? 1 : 0, x: open ? 0 : -6 }}
          transition={{ duration: 0.18 }}
          className="relative z-10 flex-1 whitespace-nowrap text-left"
        >
          {t(group.i18nKey)}
        </motion.span>
        <motion.span
          animate={{ rotate: isActive ? 0 : -90 }}
          transition={{ duration: 0.2 }}
          className={`relative z-10 mr-0.5 ${open ? 'opacity-60' : 'opacity-0'}`}
        >
          <ChevronDown size={14} />
        </motion.span>
        {!open && (
          <span
            className="pointer-events-none absolute left-full top-1/2 ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-night px-2.5 py-1.5 text-[11.5px] font-700 text-white opacity-0 shadow-lg transition group-hover:opacity-100"
          >
            {t(group.i18nKey)}
          </span>
        )}
      </button>

      <AnimatePresence initial={false}>
        {isActive && open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-1 flex flex-col gap-0.5 pb-1">
              {group.children.map((child) => (
                <NavLeaf
                  key={child.to}
                  item={child}
                  open={open}
                  label={t(child.i18nKey)}
                  inset
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Language picker (kept identical to before) ─────────────────────── */

function SidebarLanguagePicker({ open }: { open: boolean }) {
  const { i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current =
    SUPPORTED_LANGS.find((l) => l.code === i18n.resolvedLanguage) ?? SUPPORTED_LANGS[0]!;

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setMenuOpen((v) => !v)}
        className="group flex h-11 w-full items-center gap-3 rounded-2xl px-3 text-[13.5px] font-600 text-ink/65 transition hover:bg-violet/[0.06] hover:text-ink"
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
      >
        <Globe2 size={17} className="shrink-0" />
        <motion.span
          initial={false}
          animate={{ opacity: open ? 1 : 0, x: open ? 0 : -6 }}
          transition={{ duration: 0.18 }}
          className="flex items-center gap-1.5 whitespace-nowrap"
        >
          <span className="text-base leading-none">{current.flag}</span>
          <span>{current.label}</span>
        </motion.span>
        {!open && (
          <span className="pointer-events-none absolute left-full top-1/2 ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-night px-2.5 py-1.5 text-[11.5px] font-700 text-white opacity-0 shadow-lg transition group-hover:opacity-100">
            {current.flag} {current.label}
          </span>
        )}
      </button>
      <AnimatePresence>
        {menuOpen && (
          <motion.ul
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.16 }}
            role="listbox"
            className="absolute bottom-full left-0 z-50 mb-2 max-h-[360px] w-[220px] overflow-y-auto rounded-2xl border border-white/65 bg-white/95 p-1.5 shadow-[0_20px_50px_-20px_rgba(168,139,250,0.45),0_10px_24px_-12px_rgba(236,72,153,0.20)] backdrop-blur-2xl"
          >
            {SUPPORTED_LANGS.map((lang) => {
              const isActive = lang.code === current.code;
              return (
                <li key={lang.code}>
                  <button
                    onClick={() => {
                      i18n.changeLanguage(lang.code as LangCode);
                      setMenuOpen(false);
                    }}
                    className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13px] font-500 transition ${
                      isActive
                        ? 'bg-violet-500/10 text-ink'
                        : 'text-ink/75 hover:bg-violet-500/5 hover:text-ink'
                    }`}
                  >
                    <span className="text-lg leading-none">{lang.flag}</span>
                    <span className="flex-1">{lang.label}</span>
                    {isActive && <Check size={13} className="text-violet-500" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
